import { useState, useEffect } from "react";
import { Company } from "../../types/schema";
import { SUBCRIPTION_TYPE_AND_DESCRIPTION } from "../../utils/constants";
import "../../css/UserEditModal.css";
import "../../css/SiteAdminModals.css";

interface ManageSubscriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    company: Company | null;
    onSave: (data: { expirationDate: string; subscriptionType: string; modules: Record<string, boolean> }) => void;
}

const UNIQUE_MODULES = [
    { field: "initialStarterPackageEnrollment", name: "Inspections", desc: "Equipment inspection forms & reports" },
    { field: "pmModuleEnrollment", name: "Preventive Maintenance", desc: "PM scheduling & service records" },
    { field: "tiresModuleEnrollment", name: "Tire Maintenance", desc: "Tire inventory, assignment & tracking" },
    { field: "costTrackingModuleEnrollment", name: "Cost Tracking", desc: "Expense categories & cost reports" },
    { field: "trainingModuleEnrollment", name: "Safety / Training", desc: "Safety topics, compliance tracking" },
    { field: "skillTrackingModuleEnrollment", name: "User Skillsets", desc: "Employee qualifications tracking" },
];

export const ManageSubscriptionModal = ({ isOpen, onClose, company, onSave }: ManageSubscriptionModalProps) => {
    const [expDate, setExpDate] = useState("");
    const [subType, setSubType] = useState("");
    const [modules, setModules] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (company) {
            // Convert MM/DD/YYYY to YYYY-MM-DD for date input
            const exp = company.subscriptionExpiration;
            if (exp) {
                try { setExpDate(new Date(exp).toISOString().split("T")[0]); } catch { setExpDate(""); }
            }
            setSubType(company.subscriptionType);
            const m: Record<string, boolean> = {};
            UNIQUE_MODULES.forEach(({ field }) => { m[field] = !!(company as Record<string, unknown>)[field]; });
            setModules(m);
        }
    }, [company]);

    if (!isOpen || !company) return null;

    const toggleModule = (field: string) => setModules((prev) => ({ ...prev, [field]: !prev[field] }));

    return (
        <div className="uem-overlay" onClick={onClose}>
            <div className="uem-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
                <div className="uem-header">
                    <h2>Manage Subscription & Module Access</h2>
                    <button className="uem-close" onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="uem-body">
                    {/* Expiration Date */}
                    <div className="uem-date-field" style={{ marginBottom: 20 }}>
                        <label>Subscription Expiration Date</label>
                        <input type="date" value={expDate} onChange={(e) => setExpDate(e.target.value)} />
                        <p className="uem-date-hint">Setting a past date will immediately expire the account.</p>
                    </div>

                    {/* Subscription Type */}
                    <label className="uem-section-label">Subscription Type</label>
                    <div className="uem-radio-cards" style={{ marginBottom: 20 }}>
                        {SUBCRIPTION_TYPE_AND_DESCRIPTION.map((opt) => (
                            <label key={opt.value} className={`uem-radio-card ${subType === opt.value ? "selected" : ""}`}>
                                <input type="radio" name="subType" value={opt.value} checked={subType === opt.value} onChange={() => setSubType(opt.value)} />
                                <div className="uem-radio-card-content">
                                    <div className="uem-radio-card-title">{opt.label}</div>
                                    {opt.value === "paused" ? (
                                        <div className="uem-radio-card-warn">Account paused due to non-payment — access suspended</div>
                                    ) : (
                                        <div className="uem-radio-card-desc">{opt.description}</div>
                                    )}
                                </div>
                            </label>
                        ))}
                    </div>

                    {/* Module Activation */}
                    <label className="uem-section-label">Module Activation</label>
                    <p className="uem-modal-desc">Active modules appear on the company's home page. Inactive modules are visible but locked until activated.</p>
                    <div className="uem-toggle-grid">
                        {UNIQUE_MODULES.map(({ field, name, desc }) => (
                            <div key={field} className={`uem-toggle-card ${modules[field] ? "active" : ""}`}>
                                <div className="uem-toggle-card-info">
                                    <div className="uem-toggle-card-name">{name}</div>
                                    <div className="uem-toggle-card-desc">{desc}</div>
                                </div>
                                <label className="uem-toggle-switch">
                                    <input type="checkbox" checked={!!modules[field]} onChange={() => toggleModule(field)} />
                                    <span className="uem-toggle-slider" />
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="uem-footer">
                    <button type="button" className="uem-btn-cancel" onClick={onClose}>Cancel</button>
                    <button type="button" className="uem-btn-submit" onClick={() => onSave({ expirationDate: expDate, subscriptionType: subType, modules })}>
                        Save Changes
                    </button>
                </div>
            </div>
        </div>
    );
};
